import { saveAs } from "file-saver";
import ExcelJS from "exceljs";
import JSPDF from "jspdf";
import JSZip from "jszip";
import autoTable, { RowInput, UserOptions } from "jspdf-autotable";

import {
	getExcelCurrencyFormat,
	createLogger,
	formatNumber,
} from "../../utils";
import { CalendarNamespace } from "../../components/common";
import { DateRange } from "../../types/DataRange";
import IResponseWithItems from "../../types/IResponse";
import { СurrencyDefinitions } from "../../types";
import {
	formatDate,
	formatMoney,
	toExcelLocalDate,
} from "../../pages/Reports/pages/AccountingReports/utils/formatForFile";
import { Base, Language } from "..";

const logger = createLogger({ name: "AgentOrdersReport" });

type OrderItem = AgentOrdersReport.Model.Item["orders"][number];

const DATE_COLUMNS = new Set(["created", "when", "completed"]);
const MONEY_COLUMNS = new Set(["agentFeeUah", "orderAmountUah"]);

const getOrderDate = (columnId: string, order: OrderItem) => {
	if (columnId === "created") return order.createdAt;
	if (columnId === "when") return order.arrivalTime;
	if (columnId === "completed") return order.closedAt;
	return undefined;
};

const getOrderMoney = (columnId: string, order: OrderItem) =>
	columnId === "agentFeeUah" ? order.fee : order.price;

/** Value of each table column as the table cell shows it (ModelTable/components) */
const getOrderText = (
	columnId: string,
	order: OrderItem,
	statusText: Record<string, string>,
): string => {
	if (DATE_COLUMNS.has(columnId)) {
		const date = getOrderDate(columnId, order);
		return date ? formatDate({ date }) : "";
	}
	if (MONEY_COLUMNS.has(columnId)) {
		return formatNumber(getOrderMoney(columnId, order)) ?? "";
	}
	switch (columnId) {
		case "orderNumber":
			return `${order.orderNumber ?? ""}`;
		case "phone":
			return order.phone ?? "";
		case "passenger":
			return order.fullName ?? "";
		case "from":
			return order.addressFrom ?? "";
		case "to":
			return order.addressTo ?? "";
		case "note":
			return order.executorNotes ?? "";
		case "orderNote":
			return order.orderNotes ?? "";
		case "callSign":
			return order.executorCallSign ?? "";
		case "paymentType":
			return order.paymentType ?? "";
		case "status":
			return order.status ? statusText.completed : statusText.pending;
		default:
			return "";
	}
};

const getOrderExcelValue = (
	columnId: string,
	order: OrderItem,
	statusText: Record<string, string>,
): string | number | Date | null => {
	if (DATE_COLUMNS.has(columnId)) {
		return toExcelLocalDate(getOrderDate(columnId, order));
	}
	if (MONEY_COLUMNS.has(columnId)) {
		return parseFloat(formatMoney(getOrderMoney(columnId, order)));
	}
	return getOrderText(columnId, order, statusText);
};

class AgentOrdersReport extends Base {
	static fromResponse(data: any): AgentOrdersReport.Model {
		return data || [];
	}

	public static async getAgentOrdersReport(
		params: AgentOrdersReport.SearchOptions,
	): Promise<AgentOrdersReport.GetAgentOrdersReportResponse | null> {
		try {
			if (!params.dateRange) return null;

			const response = await this.request((prpc) =>
				prpc.theirsModel.report.getAgentOrdersReport(params),
			);

			logger.info("[AgentOrdersReport] getAgentOrdersReport response", {
				response,
			});
			if (!response) return null;

			return {
				...response,
				items: response?.items?.map(this.fromResponse),
			};
		} catch (error) {
			logger.error("[AgentOrdersReport] Error", error);

			return null;
		}
	}

	public static exportPDF = async (
		data: AgentOrdersReport.Model.Item[],
		options: {
			tableColumn: string[];
			columnStyles: UserOptions["columnStyles"];

			width: number;

			height: number;

			ColumnId: any;
			columnIds: any;
			textTranslations: Record<string, string>;
			headerTitle: string;
		},
	) => {
		if (!data?.length) return;
		const {
			tableColumn = [],
			width = 3500,
			height = 780,

			columnStyles,
			ColumnId,
			columnIds,
			textTranslations,
			headerTitle,
		} = options;

		const totalWidth = columnStyles
			? Object.values(columnStyles).reduce(
					(sum, { cellWidth }) => sum + +(cellWidth || 0),
					0,
			  ) +
			  Object.keys(columnStyles).length * (5.1 / 3.56) +
			  0.1 / 3
			: width;

		const realWidth = totalWidth || width;
		const doc = new JSPDF({
			orientation: "landscape",
			format: [realWidth, height],
		});

		let currentY = 10;
		let _dateFrom;
		let _dateTo;

		data.forEach(({ taxiService, orders, dateRange, counter }) => {
			doc.setFont("Roboto-Regular", "normal");

			const textCompany = taxiService?.company?.name ?? "";
			const textFill = taxiService?.name ? `, ${taxiService?.name}` : "";
			const textCounterparty = taxiService?.agentName
				? taxiService?.agentName
				: "";

			const dateFrom = formatDate({
				date: dateRange.from,
				isTitleFormat: true,
			});

			const dateTo = formatDate({
				date: dateRange.to,
				isTitleFormat: true,
			});

			if (!_dateFrom) _dateFrom = dateFrom;
			if (!_dateTo) _dateTo = dateTo;

			const pageWidth = doc.internal.pageSize.getWidth();
			const centerX = pageWidth / 2;

			const textArray = [
				`${textCompany} ${textFill}`,

				`${textCounterparty} ${textTranslations.forPeriod} ${textTranslations.from} ${dateFrom} ${textTranslations.to} ${dateTo}`,
			];

			textArray.forEach((text) => {
				const textWidth = doc.getTextWidth(text);

				const textX = centerX - textWidth / 2;

				doc.text(text, textX, currentY);

				currentY += 10;
			});

			const tableRows = orders.map(
				(order) =>
					(columnIds as string[]).map((columnId) => ({
						content: getOrderText(
							columnId,
							order,
							textTranslations,
						),
					})) as RowInput,
			);

			autoTable(doc, {
				head: [tableColumn],
				body: tableRows,
				startY: currentY,
				styles: {
					fontSize: 12,
					cellPadding: 5,
					lineWidth: 0.1,
					lineColor: [0, 0, 0],
					font: "Roboto-Regular",
					fontStyle: "normal",
				},
				columnStyles,
				headStyles: {
					fillColor: [41, 128, 185],
					textColor: [255, 255, 255],
					fontSize: 10,
				},
				margin: { top: 20 },
				tableLineWidth: 0,
				tableLineColor: [0, 0, 0],
				theme: "grid",
			});

			currentY = doc?.lastAutoTable?.finalY;

			const currency = orders?.[0]?.currency?.name || "";

			doc.setFont("Roboto-Regular", "normal");
			doc.text(
				[
					`${textTranslations.successful}: ${counter.success.count}    ${textTranslations.unsuccessful}: ${counter.filed.count}`,
					`${textTranslations.amount}, ${currency}: ${counter.success.amount}   ${textTranslations.amount}, ${currency}: ${counter.filed.amount}`,
					`${textTranslations.fee}, ${currency}: ${counter.fee}`,
				],
				30,
				currentY + 20,
			);
			currentY += 50;
		});

		const namefile = `${headerTitle ?? ""} - ${_dateFrom ?? ""} - ${
			_dateTo ?? ""
		}.pdf`;

		doc.save(namefile);
	};

	public static exportExcel = async (
		_data: AgentOrdersReport.Model.Item[],
		options: {
			tableColumn: string[];
			columnStyles: UserOptions["columnStyles"];
			width: number;
			height: number;
			ColumnId: any;
			columnIds: any;
			textTranslations: Record<string, string>;
			headerTitle: string;
			isZip: boolean;
			dateFrom: CalendarNamespace.Value;
			dateTo: CalendarNamespace.Value;
		},
	) => {
		if (!_data?.length) return;

		const {
			tableColumn = [],

			columnStyles,
			ColumnId,
			columnIds,
			textTranslations,
			headerTitle,
			isZip,
			dateFrom,
			dateTo,
		} = options;

		const _dateFrom = formatDate({
			date: dateFrom ?? new Date(),
			isTitleFormat: true,
		});
		const _dateTo = formatDate({
			date: dateTo ?? new Date(),
			isTitleFormat: true,
		});

		const namefile = `${headerTitle ?? ""} - ${_dateFrom ?? ""} - ${
			_dateTo ?? ""
		}`;

		const zip = new JSZip();
		const workbook = new ExcelJS.Workbook();

		const clearWorksheets = () => {
			workbook.worksheets.forEach((sheet) =>
				workbook.removeWorksheet(sheet.name),
			);
		};

		const createReport = async (data: AgentOrdersReport.Model.Item[]) => {
			const worksheet = workbook.addWorksheet(namefile);

			tableColumn.forEach((colIndex, index) => {
				const cellWidth: number = +(
					columnStyles?.[index]?.cellWidth ?? 80
				);

				if (typeof cellWidth === "number" && cellWidth > 0) {
					worksheet.getColumn(index + 1).width = Math.round(
						cellWidth / 2.5,
					);
				} else {
					logger.warn(
						`2 Width for column ${colIndex} is undefined or invalid: cellWidth`,
						cellWidth,
					);
				}
			});

			data.forEach(({ taxiService, orders, dateRange, counter }) => {
				const textCompany = taxiService?.company?.name ?? "";
				const textFill = taxiService?.name
					? `, ${taxiService?.name}`
					: "";
				const textCounterparty = taxiService?.agentName || "";

				const dateFrom = formatDate({
					date: dateRange.from,
					isTitleFormat: true,
				});
				const dateTo = formatDate({
					date: dateRange.to,
					isTitleFormat: true,
				});

				const addHeaderRow = (text: string) => {
					const headerRow = worksheet.addRow([text]);
					headerRow.font = {
						size: 10,
						name: "Arial",
					};
					headerRow.alignment = {
						horizontal: "left",
						vertical: "middle",
						wrapText: false,
					};

					headerRow.height = 30;

					worksheet.mergeCells(
						`A${headerRow.number}:F${headerRow.number}`,
					);

					headerRow.getCell(1).alignment = {
						horizontal: "center",
						vertical: "middle",
					};
				};

				const textheaderTable = [
					`${textCompany} ${textFill}`,
					`${textCounterparty} ${textTranslations.forPeriod} ${textTranslations.from} ${dateFrom} ${textTranslations.to} ${dateTo}`,
				];

				textheaderTable.forEach((text) => {
					addHeaderRow(text);
				});

				const titleRow = worksheet.addRow(tableColumn);
				titleRow.font = {
					name: "Calibri",
					size: 11,
					color: { argb: "ffffff" },
				};
				titleRow.alignment = {
					horizontal: "center",
					vertical: "middle",
					wrapText: true,
				};
				titleRow.fill = {
					type: "pattern",
					pattern: "solid",
					fgColor: { argb: "4baac6" },
				};
				titleRow.border = {
					top: { style: "thin", color: { argb: "FF000000" } },
					bottom: { style: "thin", color: { argb: "FF000000" } },
				};

				const maxHeight = Math.min(
					3,
					Math.max(
						...tableColumn.map((cell) => {
							if (typeof cell === "string") {
								return Math.ceil(cell.length / 10);
							}
							return 1;
						}),
					),
				);

				titleRow.height = maxHeight * 13;

				orders.forEach((order, index) => {
					const ids = columnIds as string[];
					const row: (string | number | undefined | Date | null)[] =
						ids.map((columnId) =>
							getOrderExcelValue(
								columnId,
								order,
								textTranslations,
							),
						);

					function isOdd(num: number): boolean {
						return num % 2 !== 0;
					}

					const newRow = worksheet.addRow(row);
					newRow.alignment = {
						horizontal: "center",
						vertical: "middle",
						wrapText: true,
					};
					newRow.font = { name: "Arial", size: 10 };

					ids.forEach((columnId, columnIndex) => {
						const cell = newRow.getCell(columnIndex + 1);
						if (MONEY_COLUMNS.has(columnId)) {
							const currency =
								orders?.[0]?.currency?.symbol || "₴";
							cell.numFmt = getExcelCurrencyFormat(currency);
							cell.alignment = { wrapText: true };
						}
						if (DATE_COLUMNS.has(columnId)) {
							cell.numFmt = "DD.MM.YYYY HH:mm:ss";
							cell.alignment = { wrapText: true };
						}
					});

					if (isOdd(index)) {
						newRow.fill = {
							type: "pattern",
							pattern: "solid",
							fgColor: { argb: "d9d9d9" },
						};
					}
					if (orders.length - 1 === index) {
						newRow.border = {
							bottom: {
								style: "thin",
								color: { argb: "FF000000" },
							},
						};
					}
					const maxHeight = Math.min(
						3,
						Math.max(
							...row.map((cell) => {
								if (typeof cell === "string") {
									return Math.ceil(cell.length / 10);
								}
								return 1;
							}),
						),
					);

					newRow.height = maxHeight * 13;

					newRow.alignment = {
						horizontal: "center",
						vertical: "middle",
						wrapText: true,
					};
				});

				const currency = orders?.[0]?.currency?.name || "";

				const footerOne = worksheet.addRow([
					textTranslations.successful,
					counter.success.count || 0,
					textTranslations.unsuccessful,
					counter.filed.count || 0,
				]);

				const footerTwo = worksheet.addRow([
					`${textTranslations.amount}, ${currency}:`,
					counter.success.amount || 0,
					`${textTranslations.amount}, ${currency}:`,
					counter.filed.amount || 0,
				]);

				const footerThree = worksheet.addRow([
					`${textTranslations.fee}, ${currency}:`,
					counter.fee || 0,
				]);

				footerOne.eachCell((cell) => {
					cell.border = {
						top: { style: "thin" },
						left: { style: "thin" },
						bottom: { style: "thin" },
						right: { style: "thin" },
					};
					cell.alignment = {
						horizontal: "center",
						vertical: "middle",
					};
					cell.font = { name: "Calibri", size: 11 };
					cell.fill = {
						type: "pattern",
						pattern: "solid",
						fgColor: { argb: "F9F9F9" },
					};
				});

				footerTwo.eachCell((cell) => {
					cell.border = {
						top: { style: "thin" },
						left: { style: "thin" },
						bottom: { style: "thin" },
						right: { style: "thin" },
					};
					cell.alignment = {
						horizontal: "center",
						vertical: "middle",
					};
					cell.font = { name: "Calibri", size: 11 };
					cell.fill = {
						type: "pattern",
						pattern: "solid",
						fgColor: { argb: "F9F9F9" },
					};
				});

				footerThree.eachCell((cell) => {
					cell.border = {
						top: { style: "thin" },
						left: { style: "thin" },
						bottom: { style: "thin" },
						right: { style: "thin" },
					};
					cell.alignment = {
						horizontal: "center",
						vertical: "middle",
					};
					cell.font = { name: "Calibri", size: 11 };
					cell.fill = {
						type: "pattern",
						pattern: "solid",
						fgColor: { argb: "F9F9F9" },
					};
				});

				worksheet.addRow([]);
				worksheet.addRow([]);
			});

			const buffer = await workbook.xlsx.writeBuffer();

			if (isZip) {
				zip.file(
					`${data[0]?.taxiService?.agentName ?? "Report"}.xlsx`,
					buffer,
				);
			} else {
				const blob = new Blob([buffer], {
					type: "application/octet-stream",
				});
				saveAs(blob, nameFileExcelOnly);
			}
		};

		const nameFileZip = `${namefile}.zip`;
		const nameFileExcelOnly = `${namefile}.xlsx`;

		if (isZip) {
			for (let index = 0; index < _data.length; index++) {
				clearWorksheets();
				await createReport([_data[index]]);
			}

			const zipContent = await zip.generateAsync({ type: "blob" });
			saveAs(zipContent, nameFileZip);
		} else {
			createReport(_data);
		}
	};
}

enum PaymentType {
	Card = "card",
	Cash = "cash",
	Invoice = "invoice",
	CompanyBill = "company_bill",
}

declare namespace AgentOrdersReport {
	type Model = AgentOrdersReport.Model.Item;

	interface GetAgentOrdersReportResponse extends IResponseWithItems<Model> {}

	interface SearchOptions {
		successes?: boolean;
		agentIds?: number[];
		taxiServiceIds?: number[];
		dateRange: DateRange;
		lang: Language;
	}

	namespace Model {
		interface AmountCount {
			count: number;
			amount: number;
		}
		interface Counter {
			success: AmountCount;
			filed: AmountCount;
			fee: number;
		}

		interface Order {
			id: number;
			orderNumber?: number;
			phone: string;
			checkCard: string;
			fullName: string;
			addressFrom: string;
			addressTo: string;
			orderNotes: string;
			executorNotes: string;
			createdAt: string | Date;
			arrivalTime: number;
			closedAt: string | Date;
			executorCallSign: string;
			fee: number;
			price: number;
			paymentType: PaymentType | string;
			currency: СurrencyDefinitions.Value;
			status: boolean;
		}

		interface Item {
			dateRange: {
				from: Date;
				to: Date;
			};
			taxiService: {
				id: number;
				name: string;
				company: {
					id: number;
					name: string;
				};
				agentName?: string;
			};
			orders: Order[];
			counter: Counter;
		}
	}
}

export default AgentOrdersReport;
